import * as reduxApi from 'src/utils/redux-api-v2';
import type {Dispatch, ThunkAction} from 'src/reducers';
import {ANALYTICS_API_V5} from 'src/utils/analytics-api-migration';
import type {ApiOptions, ApiPostOptions} from 'src/utils/redux-api';
import type {
  SelectedNpsChartFilterOptionsData,
  SelectedSurveyNpsFilterColumnsData,
} from 'src/types/analytics-genesis';
import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import {thunkify as flow} from 'src/utils/thunks';
import {fetching, key} from 'src/utils/redux';
import {
  npsCandidateChartColumnsQuery,
  npsCandidateTableColumnsQuery,
  npsSourceAgnosticCandidateTableColumnsQuery,
  npsSourceAgnosticClientTableColumnsQuery,
} from 'src/utils/analytics-genesis';


const analyticsOptions: ApiOptions = {apiPath: ANALYTICS_API_V5};
const analyticsApiOptions: ApiPostOptions = {apiPath: ANALYTICS_API_V5};

export const SET_CURRENT_DASHBOARD_ID =
  'analytics-nps-dashboard/set-current-dashboard';
export const SET_SELECTED_DATE_RANGE =
  'analytics-nps-dashboard/set-selected-date-range';
export const RECEIVE_CANDIDATE_NPS_TABLE_COLUMNS =
  'analytics-nps-dashboard/receive-candidate-nps-table-columns';
export const RECEIVE_CLIENT_NPS_TABLE_COLUMNS =
  'analytics-nps-dashboard/receive-client-nps-table-columns';
export const RECEIVE_CANDIDATE_NPS_CHART_COLUMNS =
  'analytics-nps-dashboard/receive-candidate-nps-chart-columns';
export const RECEIVE_CLIENT_NPS_CHART_COLUMNS =
  'analytics-nps-dashboard/receive-client-nps-chart-columns';

type SetCurrentDashboardIdAction = {
  type: typeof SET_CURRENT_DASHBOARD_ID;
  payload: string | null | undefined;
};

export type SetSelectedDateRangeAction = {
  type: typeof SET_SELECTED_DATE_RANGE;
  payload: DateRangeWithTimezone;
};

export type ReceiveCandidateNPSTableColumnsAction = {
  type: typeof RECEIVE_CANDIDATE_NPS_TABLE_COLUMNS;
  payload: SelectedSurveyNpsFilterColumnsData;
};

export type ReceiveClientNPSTableColumnsAction = {
  type: typeof RECEIVE_CLIENT_NPS_TABLE_COLUMNS;
  payload: SelectedSurveyNpsFilterColumnsData;
};

export type ReceiveCandidateNPSChartColumnsAction = {
  type: typeof RECEIVE_CANDIDATE_NPS_CHART_COLUMNS;
  payload: Array<string>;
};

export type ReceiveClientNPSChartColumnsAction = {
  type: typeof RECEIVE_CLIENT_NPS_CHART_COLUMNS;
  payload: Array<string>;
};

export type AnalyticsNPSDashboardActions =
  | SetCurrentDashboardIdAction
  | SetSelectedDateRangeAction
  | ReceiveCandidateNPSTableColumnsAction
  | ReceiveClientNPSTableColumnsAction
  | ReceiveCandidateNPSChartColumnsAction
  | ReceiveClientNPSChartColumnsAction;

export const setCurrentDashboardId = (
  payload: string,
): SetCurrentDashboardIdAction => ({
  type: SET_CURRENT_DASHBOARD_ID,
  payload,
});

export const setSelectedDateRange = (
  payload: DateRangeWithTimezone,
): SetSelectedDateRangeAction => ({
  type: SET_SELECTED_DATE_RANGE,
  payload,
});

// Alias for backward compatibility
export const setDateRange = setSelectedDateRange;

export const receiveCandidateNPSTableColumns = (
  payload: SelectedSurveyNpsFilterColumnsData,
): ReceiveCandidateNPSTableColumnsAction => ({
  type: RECEIVE_CANDIDATE_NPS_TABLE_COLUMNS,
  payload,
});

export const receiveClientNPSTableColumns = (
  payload: SelectedSurveyNpsFilterColumnsData,
): ReceiveClientNPSTableColumnsAction => ({
  type: RECEIVE_CLIENT_NPS_TABLE_COLUMNS,
  payload,
});

export const receiveCandidateNPSChartColumns = (
  payload: Array<string>,
): ReceiveCandidateNPSChartColumnsAction => ({
  type: RECEIVE_CANDIDATE_NPS_CHART_COLUMNS,
  payload,
});

export const receiveClientNPSChartColumns = (
  payload: Array<string>,
): ReceiveClientNPSChartColumnsAction => ({
  type: RECEIVE_CLIENT_NPS_CHART_COLUMNS,
  payload,
});

export const getCandidateNPSTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getCandidateNPSTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: SelectedSurveyNpsFilterColumnsData = await dispatch(
        reduxApi.get(
          '/candidate-nps/column-selection',
          npsSourceAgnosticCandidateTableColumnsQuery,
          analyticsOptions,
        ),
      );
      dispatch(receiveCandidateNPSTableColumns(payload));
    } catch (error: any) {
      console.error('Error fetching candidate NPS table columns:', error);
    }
  });

export const saveCandidateNPSSelectedFilterColumns: (
  columnData: SelectedSurveyNpsFilterColumnsData,
) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        '/candidate-nps/column-selection',
        columnData,
        {},
        analyticsApiOptions,
      ),
    ),
);

export const getClientNPSTableFilterColumn: () => ThunkAction<unknown> = flow(
  key(() => 'getClientNPSTableFilterColumn'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: SelectedSurveyNpsFilterColumnsData = await dispatch(
      reduxApi.get(
        '/client-nps/column-selection',
        npsSourceAgnosticClientTableColumnsQuery,
        analyticsOptions,
      ),
    );
    dispatch(receiveClientNPSTableColumns(payload));
  } catch (error: any) {
    console.error('Error fetching client nps column filter:', error);
  }
});

export const saveClientNPSSelectedFilterColumns: (
  columnData: SelectedSurveyNpsFilterColumnsData,
) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        '/client-nps/column-selection',
        columnData,
        {},
        analyticsApiOptions,
      ),
    ),
);

export const getCandidateNPSChartFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getCandidateNPSChartFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: {
        views: Array<string> | null;
      } = await dispatch(
        reduxApi.get(
          '/candidate-nps/save-plot-view',
          npsSourceAgnosticCandidateTableColumnsQuery,
          analyticsOptions,
        ),
      );

      const uniqueViews = Array.from(new Set(payload.views ?? []));

      dispatch(receiveCandidateNPSChartColumns(uniqueViews.slice(0, 10)));
    } catch (error: any) {
      console.error('Error fetching candidate nps chart filter:', error);
    }
  });

export const saveCandidateNPSSelectedChartFilter: (
  columnData: SelectedNpsChartFilterOptionsData,
) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) => {
    const {dashboard, product, sub_dashboard, views} = columnData;
    return dispatch(
      reduxApi.post(
        '/candidate-nps/save-plot-view',
        {dashboard, product, sub_dashboard, views},
        {},
        analyticsApiOptions,
      ),
    );
  },
);

export const getClientNPSChartFilterColumn: () => ThunkAction<unknown> = flow(
  key(() => 'getClientNPSChartFilterColumn'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: {
      views: Array<string> | null;
    } = await dispatch(
      reduxApi.get(
        '/client-nps/save-plot-view',
        npsSourceAgnosticClientTableColumnsQuery,
        analyticsOptions,
      ),
    );

    const uniqueViews = Array.from(new Set(payload.views ?? []));

    dispatch(receiveClientNPSChartColumns(uniqueViews.slice(0, 10)));
  } catch (error: any) {
    console.error('Error fetching candidate nps chart filter:', error);
  }
});

export const saveClientNPSSelectedChartFilter: (
  columnData: SelectedNpsChartFilterOptionsData,
) => ThunkAction<unknown> = flow(
  key(
    (columnData) =>
      `saveClientNPSSelectedChartFilter-${JSON.stringify(columnData)}`,
  ),
  fetching(),
)((columnData) => async (dispatch: Dispatch) => {
  const {dashboard, product, sub_dashboard, views} = columnData;
  return dispatch(
    reduxApi.post(
      '/client-nps/save-plot-view',
      {dashboard, product, sub_dashboard, views},
      {},
      analyticsApiOptions,
    ),
  );
});
